import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  People,
  Search,
  Visibility,
  Phone,
  Email,
  Warning,
  LocalHospital,
  ArrowBack,
  PersonAdd,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import apiClient from '../lib/axios';
import { PatientCreateDialog } from '../components/PatientCreateDialog';

interface PatientAllergy {
  id: string;
  rxcui: string | null;
  name: string;
  addedByDoctor: {
    id: string;
    firstName: string;
    lastName: string;
  };
  createdAt: string;
}

interface Patient {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  allergies?: PatientAllergy[];
  chronicConditions?: string;
  lastVisit?: string;
  prescriptionCount: number;
}

export const PatientList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const { data: patients, isLoading, error } = useQuery({
    queryKey: ['patients'],
    queryFn: async () => {
      const { data } = await apiClient.get<Patient[]>('/patients');
      return data;
    },
  });

  const filteredPatients = React.useMemo(() => {
    if (!patients) return [];
    if (!searchTerm) return patients;

    const lowerSearch = searchTerm.toLowerCase();
    return patients.filter(
      (patient) =>
        patient.name.toLowerCase().includes(lowerSearch) ||
        patient.phone?.includes(searchTerm) ||
        patient.email?.toLowerCase().includes(lowerSearch)
    );
  }, [patients, searchTerm]);

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <Alert severity="error">{t('patients.failedToLoad')}</Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Paper sx={{ p: { xs: 2, sm: 4 } }}>
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <People sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Typography variant="h4" component="h1" fontWeight="bold">
                  {t('patients.myPatients')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<PersonAdd />}
                  onClick={() => setCreateDialogOpen(true)}
                >
                  {t('patients.createPatient')}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBack />}
                  onClick={() => navigate('/doctor/dashboard')}
                  sx={{ display: { xs: 'none', sm: 'flex' } }}
                >
                  {t('patients.backToDashboard')}
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                fullWidth
                placeholder={t('patients.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ maxWidth: { sm: '400px' } }}
              />
              <Chip
                label={`${filteredPatients.length} ${filteredPatients.length === 1 ? t('patients.patient') : t('patients.patients')}`}
                color="primary"
                variant="outlined"
              />
            </Box>
          </Box>

          {filteredPatients.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <People sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {searchTerm ? t('patients.noResults') : t('patients.noPatients')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {searchTerm
                  ? t('patients.tryAdjusting')
                  : t('patients.patientsWillAppear')}
              </Typography>
              {!searchTerm && (
                <Button
                  variant="contained"
                  startIcon={<PersonAdd />}
                  onClick={() => setCreateDialogOpen(true)}
                >
                  {t('patients.createFirstPatient')}
                </Button>
              )}
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('patients.name')}</TableCell>
                    <TableCell>{t('patients.contact')}</TableCell>
                    <TableCell>{t('patients.medicalInfo')}</TableCell>
                    <TableCell>{t('patients.lastVisit')}</TableCell>
                    <TableCell align="center">{t('patients.prescriptions')}</TableCell>
                    <TableCell align="right">{t('patients.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPatients.map((patient) => (
                    <TableRow
                      key={patient.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/doctor/patients/${patient.id}`)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {patient.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Phone sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {patient.phone}
                            </Typography>
                          </Box>
                          {patient.email && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Email sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {patient.email}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {patient.allergies && patient.allergies.length > 0 && (
                            <Chip
                              icon={<Warning />}
                              label={`Allergies: ${patient.allergies.map(a => a.name).join(', ')}`}
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          )}
                          {patient.chronicConditions && (
                            <Chip
                              icon={<LocalHospital />}
                              label={`Conditions: ${patient.chronicConditions}`}
                              size="small"
                              color="info"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          )}
                          {(!patient.allergies || patient.allergies.length === 0) && !patient.chronicConditions && (
                            <Typography variant="caption" color="text.disabled">
                              {t('patients.noneRecorded')}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {patient.lastVisit ? (
                          <Typography variant="caption" color="text.secondary">
                            {formatDistanceToNow(new Date(patient.lastVisit), { addSuffix: true })}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            {t('patients.never')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={patient.prescriptionCount}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={t('patients.viewDetails')}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/doctor/patients/${patient.id}`);
                            }}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* Create Patient Dialog */}
      <PatientCreateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={(patientId) => navigate(`/doctor/patients/${patientId}`)}
      />
    </Container>
  );
};
